import { Collapse as BlueprintCollapse } from '@blueprintjs/core';
import {
  ChevronDown,
  ChevronUp,
  Renew,
  WarningFilled,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import Markdown from 'react-markdown';
import {
  Body,
  Button,
  LoadingIcon,
  OutlinedIconButton,
  StaticBadge,
  Tooltip,
} from 'vet-bones/design-system/components';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectSidebarVoiceboxSummaryActions } from 'src/containers/ProjectSidebar/components/ProjectSidebarVoiceboxSummaryActions';
import { projectSelector } from 'src/selectors/project';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import { projectVoiceboxSummaryIssuesSelector } from 'src/selectors/projectVoiceboxSummaryIssues';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export const ProjectSidebarVoiceboxSummary: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );

  const { evaluateSummary, isLoadingSummary } = useEvaluateSummary();

  const { count } = useReduxSelector(
    projectVoiceboxSummaryIssuesSelector(projectId)
  );

  const evaluation = project?.voiceboxEvaluationSummary;

  const isOpenState = useReduxSelector(
    (state) => state.ui.isVoiceboxDrawerSummaryOpen
  );
  const isOpen = Boolean(evaluation && isOpenState);
  const isOutdated =
    !evaluation ||
    !project?.voiceboxEvaluatedSummary ||
    questions.some(
      (question) =>
        !question.evaluation ||
        !project.voiceboxEvaluatedCompetencyQuestions?.includes(question.id)
    );

  return (
    <div
      className={classNames('sd-voicebox-summary', {
        'sd-voicebox-summary--open': isOpen,
      })}
    >
      <div className="sd-voicebox-summary-header">
        <div
          className={classNames('sd-voicebox-summary-title', {
            'sd-voicebox-summary-title--interactive': evaluation && !isOpen,
          })}
        >
          <OutlinedIconButton
            disabled={!evaluation}
            elevated
            leftIcon={isOpen ? ChevronDown : ChevronUp}
            onClick={() =>
              dispatch(
                uiSlice.actions.toggledVoiceboxDrawerSummary({
                  isOpen: !isOpen,
                })
              )
            }
            testId={TEST_IDS.voicebox.summary.toggleButton}
          />
          <Body size="lg">{copy.components.voicebox.summary.title}</Body>
          {isOutdated ? (
            <Tooltip
              description={copy.components.voicebox.summary.evaluate}
              disabled={isLoadingSummary}
            >
              <Button
                disabled={isLoadingSummary}
                leftIcon={isLoadingSummary ? LoadingIcon : Renew}
                onClick={() => {
                  evaluateSummary(project);
                }}
                size="xs"
                testId={TEST_IDS.voicebox.summary.refreshButton}
                variant="ghost"
              />
            </Tooltip>
          ) : null}
        </div>
        <div className="sd-voicebox-summary-actions">
          {!isOpen && count ? (
            <StaticBadge
              leftIcon={WarningFilled}
              size="lg"
              testId={TEST_IDS.voicebox.summary.issuesCountBadge}
              type="error"
              variant="muted-gray"
            >
              {count}
            </StaticBadge>
          ) : null}
        </div>
      </div>
      {evaluation ? (
        <div className="sd-voicebox-summary-body-scroll">
          <div className="sd-voicebox-summary-body-list">
            <BlueprintCollapse isOpen={isOpen}>
              <div className="sd-voicebox-summary-body">
                <Body className="sd-voicebox-summary-text" size="md">
                  <Markdown className="vb-voicebox-markdown">
                    {evaluation.summary}
                  </Markdown>
                </Body>
                <ProjectSidebarVoiceboxSummaryActions />
              </div>
            </BlueprintCollapse>
          </div>
        </div>
      ) : null}
    </div>
  );
};
